import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import type { SessionRow } from '../../lib/api.js';

interface Alert {
  id: string;
  kind: 'stalled' | 'conflict' | 'unreachable';
  label: string;
  href: string;
  color: string;
  glow: string;
}

interface Props {
  sessions: SessionRow[];
  conflictCount: number;
  oldestConflictAt: string | null;
}

export function AttentionTray({ sessions, conflictCount, oldestConflictAt }: Props) {
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  const now = Date.now();
  const STALL_MS = 5 * 60 * 1000;

  const alerts: Alert[] = [];

  const stalled = sessions.filter((s) => {
    if (s.status !== 'running') return false;
    const ref = (s as unknown as { lastHeartbeatAt?: string | null }).lastHeartbeatAt ?? s.startedAt;
    if (!ref) return false;
    return now - new Date(ref).getTime() > STALL_MS;
  });
  if (stalled.length > 0) {
    alerts.push({
      id: 'stalled',
      kind: 'stalled',
      label: `${stalled.length} stalled session${stalled.length === 1 ? '' : 's'} — no heartbeat > 5 min`,
      href: '/imports',
      color: 'var(--accent-yellow)',
      glow: 'rgba(255,209,102,0.25)',
    });
  }

  if (conflictCount > 0) {
    const age = oldestConflictAt
      ? Math.round((now - new Date(oldestConflictAt).getTime()) / 3600000)
      : null;
    alerts.push({
      id: 'conflict',
      kind: 'conflict',
      label: `${conflictCount} open conflict${conflictCount === 1 ? '' : 's'}${age !== null ? ` — oldest ${age}h ago` : ''}`,
      href: '/conflicts',
      color: 'var(--accent-red)',
      glow: 'rgba(238,93,80,0.25)',
    });
  }

  const visible = alerts.filter((a) => !dismissed.has(a.id));
  if (visible.length === 0) return null;

  return (
    <div
      className="sticky top-0 z-40 flex flex-wrap items-center gap-2 px-6 py-2.5"
      style={{
        background: 'rgba(2,14,17,0.85)',
        backdropFilter: 'blur(12px)',
        borderBottom: '1px solid rgba(255,209,102,0.2)',
      }}
    >
      <span className="text-[10px] font-bold uppercase tracking-widest mr-1" style={{ color: 'var(--accent-yellow)' }}>
        ⚠ Attention
      </span>
      {visible.map((a) => (
        <div key={a.id} className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => void navigate({ to: a.href as '/' })}
            className="rounded-full px-3 py-1 text-xs font-semibold transition-opacity hover:opacity-80"
            style={{
              background: a.glow,
              color: a.color,
              border: `1px solid ${a.color}55`,
              boxShadow: `0 0 8px ${a.glow}`,
            }}
          >
            {a.label}
          </button>
          <button
            type="button"
            onClick={() => setDismissed((p) => new Set([...p, a.id]))}
            className="text-[10px] opacity-50 hover:opacity-100"
            style={{ color: a.color }}
            aria-label="Dismiss"
          >
            ✕
          </button>
        </div>
      ))}
    </div>
  );
}
